import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { eventConfig } from "@/config/eventConfig";
import { captureAttribution, getAdviserName } from "@/lib/attribution";
import { submitRegistration } from "@/lib/registrationService";
import { analytics, newMetaEventId } from "@/lib/analytics";
import { buildPrefill, onSelfCheckChange, UNSURE_PREFIX } from "@/lib/selfCheck";
import { StepOne } from "@/components/registration/StepOne";
import { StepTwo } from "@/components/registration/StepTwo";
import { SuccessCard } from "@/components/registration/SuccessCard";
import { REG_INPUT_STYLES } from "@/components/registration/FormPrimitives";
import {
  EMPTY_FORM,
  validateStepOne,
  buildRegistrationPayload,
  registrationErrorMessage,
} from "@/components/registration/formLogic";

const STEPS = [
  { n: 1, label: "Reserve your seat" },
  { n: 2, label: "Personalise" },
];

const Stepper = ({ step }) => (
  <div className="flex items-center gap-2 mb-6">
    {STEPS.map(({ n, label }) => (
      <React.Fragment key={n}>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold"
            style={{
              background: step >= n ? "#071524" : "rgba(17,42,67,0.08)",
              color: step >= n ? "#F7B838" : "#647384",
            }}
          >
            {n}
          </span>
          <span className="text-sm font-medium text-[#122131]">{label}</span>
        </div>
        {n === 1 && <div className="flex-1 h-px bg-[rgba(17,42,67,0.12)]" />}
      </React.Fragment>
    ))}
  </div>
);

/**
 * Reusable registration form + success card.
 * Used both in the on-page Registration section and inside the RegistrationModal popup.
 *
 * Props:
 *  - inModal (bool): when true, skips the on-page scroll behaviour on success.
 *  - onSuccess (fn): optional callback fired after a successful registration.
 */
const RegistrationForm = ({ inModal = false, onSuccess }) => {
  const { registration } = eventConfig;

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // stores saved registration
  const [attribution, setAttribution] = useState({});

  // Attribution (UTMs / referral code) is captured once on mount and the
  // adviser is pre-filled from the referral link.
  useEffect(() => {
    const attr = captureAttribution();
    setAttribution(attr);
    const code = attr.referral_code || "";
    if (code) setForm((f) => ({ ...f, invited_by: getAdviserName(code) || code }));
  }, []);

  // Self-check prefill: the visitor's "Not sure" answers from the Watch
  // section become the starting text of "Biggest CPF Question". We only ever
  // overwrite text we wrote ourselves, so a visitor's own edits are kept.
  const lastPrefill = useRef("");
  useEffect(() => {
    const apply = (answers) => {
      const text = buildPrefill(answers);
      setForm((f) => {
        const untouched = !f.biggest_question || f.biggest_question === lastPrefill.current;
        if (!untouched) return f;
        lastPrefill.current = text;
        return { ...f, biggest_question: text };
      });
    };
    apply(undefined);
    return onSelfCheckChange(apply);
  }, []);
  const questionPrefilled = (form.biggest_question || "").startsWith(UNSURE_PREFIX);

  const referralCode = attribution.referral_code || "";
  const adviserName = referralCode ? getAdviserName(referralCode) : "";

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const runStepOneValidation = () => {
    const e = validateStepOne(form);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goStep2 = () => {
    if (runStepOneValidation()) {
      analytics.formStepComplete({ step: 1 });
      setStep(2);
    }
  };

  const step1Started = useRef(false);
  const markStarted = () => {
    if (!step1Started.current) {
      step1Started.current = true;
      analytics.formStart({});
    }
  };

  const handleSubmit = async () => {
    if (!runStepOneValidation()) {
      setStep(1);
      return;
    }
    if (!form.age_range) {
      setErrors((e) => ({ ...e, age_range: "Please select your age range." }));
      return;
    }
    if (!consent) {
      setConsentError(true);
      return;
    }
    setSubmitting(true);
    analytics.registrationSubmit({ event_id: eventConfig.eventId });
    // One id shared by the browser Pixel and the server-side Conversions API
    // event so Meta deduplicates them (counts once, survives ad-blockers/iOS).
    const metaEventId = newMetaEventId();
    const payload = buildRegistrationPayload({ form, attribution, referralCode, metaEventId });

    try {
      const saved = await submitRegistration(payload);
      // Meta standard "CompleteRegistration" conversion (mapped in analytics.js).
      analytics.registrationSuccess(
        {
          id: saved.id,
          content_name: "CPF Retirement Planning Seminar",
          content_category: "seminar_registration",
          currency: "SGD",
          value: 0,
          status: "completed",
          referral_code: payload.attribution.referral_code || undefined,
        },
        saved.meta_event_id || metaEventId
      );
      setSuccess(saved);
      if (!inModal) {
        const anchor = document.getElementById("register");
        if (anchor) window.scrollTo({ top: anchor.offsetTop - 60, behavior: "smooth" });
      }
      if (typeof onSuccess === "function") onSuccess(saved);
    } catch (err) {
      const msg = registrationErrorMessage(err);
      analytics.registrationError({ status: err?.response?.status, message: msg });
      toast.error(msg);
      // Do NOT clear the form on failure.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-[24px] bg-white p-6 sm:p-8 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
      {success ? (
        <SuccessCard saved={success} />
      ) : (
        <div data-testid="registration-form">
          <Stepper step={step} />
          {step === 1 ? (
            <StepOne form={form} errors={errors} onField={setField} onFocus={markStarted} onNext={goStep2} />
          ) : (
            <StepTwo
              registration={registration}
              form={form}
              errors={errors}
              onField={setField}
              referralCode={referralCode}
              adviserName={adviserName}
              questionPrefilled={questionPrefilled}
              consent={consent}
              consentError={consentError}
              onConsentChange={(v) => { setConsent(v); setConsentError(false); }}
              submitting={submitting}
              onBack={() => setStep(1)}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      )}
      <style>{REG_INPUT_STYLES}</style>
    </div>
  );
};

export default RegistrationForm;
